import { GetFormPrice } from "./getFormPrice.js";
/**
 * @returns how many items of `[start, end)` positions are in `[from, to)` range
 */
const countOverlap = (start, end, from, to) => Math.max(0, Math.min(end, to) - Math.max(start, from));
const tierCountOffset = (scope, context) => {
    var _a, _b, _c, _d;
    if (scope === "category-format")
        return (_b = (_a = context.categoryItemsBefore) === null || _a === void 0 ? void 0 : _a.sameFormat) !== null && _b !== void 0 ? _b : 0;
    if (scope === "category-all")
        return (_d = (_c = context.categoryItemsBefore) === null || _c === void 0 ? void 0 : _c.allFormats) !== null && _d !== void 0 ? _d : 0;
    return 0;
};
/**
 * Remember to update this function in the aggregation schema
 * @param provider
 * @param context
 * @returns
 */
export const provideValue = (provider, context) => {
    var _a;
    if (typeof provider === "object") {
        switch (provider.type) {
            case "constant":
                return (_a = provider.value) !== null && _a !== void 0 ? _a : 0;
            case "revert-provider":
                return -provideValue(provider.value, context);
            case "sum-provider":
                return provider.values.reduce((acc, p) => acc + provideValue(p, context), 0);
            case "condition-provider":
                const testedValue = provideValue(provider.testedValue, context);
                const greaterValue = provideValue(provider.greaterThan, context);
                if (testedValue > greaterValue) {
                    return provideValue(provider.whenTrue, context);
                }
                else {
                    return provideValue(provider.whenFalse, context);
                }
            case "count-provider":
                return context.orderedFormats.find((format) => format.name === provider.formatName).count;
            case "price-provider":
                const format = context.orderedFormats.find((f) => f.name === provider.formatName);
                return provideValue(format.pricePerItem, context) * format.count;
            case "all-orderd-image-formats-sum-provider":
                const countSum = context.imageOrdersExcludedCurrentFormat.reduce((acc, item) => acc + item.formats.reduce((a, c) => a + c.count, 0), 0);
                return countSum;
            case "per-item":
                return context.quantity * provideValue(provider.value, context);
            case "multiply-provider":
                return (provideValue(provider.value1, context) *
                    provideValue(provider.value2, context));
            case "per-item-tiers-provider":
                // Ordered items take positions [from, to) counted in the provider's scope
                const from = tierCountOffset(provider.countScope, context);
                const to = from + context.quantity;
                let price = 0;
                let tierStart = 0;
                for (const tier of provider.tiers) {
                    const tierEnd = tierStart + tier.next;
                    const count = countOverlap(tierStart, tierEnd, from, to);
                    if (count > 0)
                        price += provideValue(tier.price, context) * count;
                    tierStart = tierEnd;
                }
                const countLeft = countOverlap(tierStart, Infinity, from, to);
                if (countLeft > 0)
                    price += provideValue(provider.priceForAllNext, context) * countLeft;
                return price;
            default:
                throw new Error("Invalid Int price provider");
        }
    }
    else {
        return provider;
    }
};
export const providePriceForFormat = (provider, context) => {
    if (typeof provider === "number") {
        const format = context.orderedFormats.find((o) => o.name === context.currentFormatName);
        return format.count * provider;
    }
    return provideValue(provider, context);
};
export const providePriceForExtraOffer = (orderedOffer) => {
    var _a, _b;
    return providePriceForFormat(orderedOffer.price, {
        currentFormatName: "default",
        imageOrdersExcludedCurrentFormat: [],
        quantity: (_a = orderedOffer.count) !== null && _a !== void 0 ? _a : 1,
        orderedFormats: [
            {
                name: "default",
                pricePerItem: orderedOffer.price,
                count: (_b = orderedOffer.count) !== null && _b !== void 0 ? _b : 1,
            },
        ],
    });
};
/**
 * It is used to return orders of one of the image with specific format excluded usefull when using `provideValue`
 * @param orders
 * @param imageIndexToInclude
 * @param offerIndex
 * @param formatNameToExclude
 * @returns
 */
export const provideImageOrdersExcludedCurrentFormat = (orders, imageIndexToInclude, offerIndex, formatNameToExclude) => {
    return orders
        .filter((o) => o.imageIndex.toString() === imageIndexToInclude.toString())
        .map((o) => ({
        //we are filtering formats only if the order is exactly the same
        formats: o.offerIndex === offerIndex ?
            o.orderedFormats.filter((f, i) => f.name !== formatNameToExclude)
            : o.orderedFormats,
    }));
};
/**
 * Counts items ordered in the same category before the given format.
 * Orders are counted in their stored order, then formats in their order within the image,
 * so items ordered earlier take the first price tiers.
 * @param orders all orders of the group
 * @param currentOrder order containing the format
 * @param formatName name of the format
 */
export const provideCategoryItemsBefore = (orders, currentOrder, formatName) => {
    var _a;
    const result = { sameFormat: 0, allFormats: 0 };
    for (const order of orders) {
        if (order.categoryIndex !== currentOrder.categoryIndex)
            continue;
        const isCurrentOrder = order.offerIndex === currentOrder.offerIndex &&
            order.imageIndex.toString() === currentOrder.imageIndex.toString();
        for (const format of (_a = order.orderedFormats) !== null && _a !== void 0 ? _a : []) {
            if (isCurrentOrder && format.name === formatName)
                return result;
            result.allFormats += format.count;
            if (order.offerIndex === currentOrder.offerIndex &&
                format.name === formatName)
                result.sameFormat += format.count;
        }
    }
    return result;
};
export const getFormatsAndExtraOfferPriceSum = (order, components) => {
    var _a;
    const { orders, oneTimeOrders, flowFormInputs } = order;
    return (GetFormPrice(flowFormInputs, components) +
        orders.reduce((acc, img) => acc +
            img.orderedFormats.reduce((acc, format) => acc +
                providePriceForFormat(format.pricePerItem, {
                    currentFormatName: format.name,
                    quantity: format.count,
                    orderedFormats: img.orderedFormats,
                    imageOrdersExcludedCurrentFormat: provideImageOrdersExcludedCurrentFormat(order.orders, img.imageIndex, img.offerIndex, format.name),
                    categoryItemsBefore: provideCategoryItemsBefore(order.orders, img, format.name),
                }), 0), 0) +
        ((_a = oneTimeOrders === null || oneTimeOrders === void 0 ? void 0 : oneTimeOrders.reduce((acc, orderedOffer) => {
            var _a, _b;
            return acc +
                providePriceForFormat(orderedOffer.price, {
                    currentFormatName: "default",
                    imageOrdersExcludedCurrentFormat: [],
                    quantity: (_a = orderedOffer.count) !== null && _a !== void 0 ? _a : 1,
                    orderedFormats: [
                        {
                            name: "default",
                            pricePerItem: orderedOffer.price,
                            count: (_b = orderedOffer.count) !== null && _b !== void 0 ? _b : 1,
                        },
                    ],
                });
        }, 0)) !== null && _a !== void 0 ? _a : 0));
};
